import React, { useState, useRef, useEffect } from "react";

const S = {
  page:{minHeight:"100vh",background:"#0a0f1e",color:"#e8edf5",fontFamily:"'DM Sans',sans-serif",position:"relative"},
  gridBg:{position:"fixed",inset:0,pointerEvents:"none",backgroundImage:"linear-gradient(rgba(255,255,255,0.02) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.02) 1px,transparent 1px)",backgroundSize:"48px 48px"},
  wrap:{maxWidth:1300,margin:"0 auto",padding:"40px 24px",position:"relative"},
  ghost:{background:"rgba(255,255,255,0.07)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:8,color:"#aabbcc",cursor:"pointer",fontSize:12,padding:"7px 14px"},
  badge:{display:"inline-block",padding:"5px 14px",borderRadius:20,fontSize:11,fontWeight:600,letterSpacing:1,textTransform:"uppercase"},
  btn:{color:"#fff",border:"none",borderRadius:10,padding:"12px 28px",fontSize:14,fontWeight:600,cursor:"pointer"},
  input:{width:"100%",background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:8,padding:"9px 13px",color:"#e8edf5",fontSize:13,outline:"none",boxSizing:"border-box"},
  card:{background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:12,padding:"18px"},
  label:{display:"block",color:"#8899aa",fontSize:12,marginBottom:6,fontWeight:600},
};

const COLOR = "#F39C12";
const COST_PER_1M_INPUT  = 0.075; // USD
const COST_PER_1M_OUTPUT = 0.30;
const USD_TO_INR = 84;

function calcCost(inputTokens, outputTokens) {
  const usd = (inputTokens/1e6)*COST_PER_1M_INPUT + (outputTokens/1e6)*COST_PER_1M_OUTPUT;
  return { usd: +usd.toFixed(6), inr: +(usd * USD_TO_INR).toFixed(4) };
}

export default function ListingTool({ user, onBack }) {
  const [step, setStep] = useState("setup");
  const [listingRows, setListingRows] = useState([]);
  const [refContent, setRefContent] = useState(null);
  const [keywords, setKeywords] = useState([]);
  const [refUrls, setRefUrls] = useState(["","","",""]);
  const [platform, setPlatform] = useState("amazon");
  const [guides, setGuides] = useState({});
  const [generated, setGenerated] = useState({});
  const [generating, setGenerating] = useState({});
  const [errors, setErrors] = useState({});
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState("");
  const [selectedSku, setSelectedSku] = useState(null);
  const [editMode, setEditMode] = useState({});
  const [downloadingAll, setDownloadingAll] = useState(false);
  // Usage tracking
  const [usage, setUsage] = useState({ inputTokens:0, outputTokens:0, calls:0 });
  // Chat
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatting, setChatting] = useState(false);
  const chatEndRef = useRef(null);

  const listingRef  = useRef();
  const refRef      = useRef();
  const kwRef       = useRef();
  const amzGuideRef = useRef();
  const fkGuideRef  = useRef();
  const msGuideRef  = useRef();

  useEffect(() => { chatEndRef.current?.scrollIntoView({behavior:"smooth"}); }, [chatMessages]);

  const handleParse = async () => {
    if (!listingRef.current?.files[0]) { setParseError("Please upload your listing Excel."); return; }
    setParsing(true); setParseError("");
    const fd = new FormData();
    fd.append("listing", listingRef.current.files[0]);
    if (refRef.current?.files[0])      fd.append("reference",     refRef.current.files[0]);
    if (kwRef.current?.files[0])       fd.append("keywords",      kwRef.current.files[0]);
    if (amzGuideRef.current?.files[0]) fd.append("amazon_guide",  amzGuideRef.current.files[0]);
    if (fkGuideRef.current?.files[0])  fd.append("flipkart_guide",fkGuideRef.current.files[0]);
    if (msGuideRef.current?.files[0])  fd.append("meesho_guide",  msGuideRef.current.files[0]);
    try {
      const r = await fetch("/api/listing/parse", { method:"POST", body:fd });
      const d = await r.json();
      if (!d.ok) throw new Error(d.error);
      setListingRows(d.rows); setRefContent(d.refContent); setKeywords(d.keywords||[]); setGuides(d.guides||{});
      setStep("preview");
    } catch(e) { setParseError(e.message); }
    setParsing(false);
  };

  const generateSku = async (row) => {
    setGenerating(g=>({...g,[row.sku]:true}));
    setErrors(e=>({...e,[row.sku]:null}));
    try {
      // Get image URLs from server
      let imageUrls = [];
      try {
        const r = await fetch(`/api/images?search=${encodeURIComponent(row.sku)}`);
        const d = await r.json();
        imageUrls = (d.rows||[]).filter(img=>img.sku===row.sku).map(img=>img.url);
      } catch(_) {}

      const r = await fetch("/api/listing/generate-sku", {
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body: JSON.stringify({
          sku: row.sku, packageIncludes: row.packageIncludes,
          price: row.price, mrp: row.mrp,
          keywords, referenceContent: refContent,
          referenceUrls: refUrls.filter(Boolean), imageUrls,
          platform, guides, username: user,
        }),
      });
      const d = await r.json();
      if (!d.ok) throw new Error(d.error);

      // Update usage
      if (d.usage) {
        setUsage(u=>({
          inputTokens:  u.inputTokens  + (d.usage.inputTokens||0),
          outputTokens: u.outputTokens + (d.usage.outputTokens||0),
          calls: u.calls + 1,
        }));
      }

      setGenerated(g=>({...g,[row.sku]:{ ...d.content, imageUrls }}));
      setSelectedSku(row.sku);
    } catch(e) { setErrors(er=>({...er,[row.sku]:e.message})); }
    setGenerating(g=>({...g,[row.sku]:false}));
  };

  const generateAll = async () => {
    for (const row of listingRows) {
      if (!generated[row.sku]) await generateSku(row);
    }
  };

  const sendChat = async () => {
    if (!chatInput.trim() || chatting) return;
    const userMsg = chatInput.trim();
    setChatInput("");
    setChatMessages(m=>[...m,{role:"user",text:userMsg}]);
    setChatting(true);

    // Build context from selected SKU
    const sel = selectedSku ? listingRows.find(r=>r.sku===selectedSku) : null;
    const genData = selectedSku ? generated[selectedSku] : null;
    const imageLinksText = genData?.imageUrls?.length > 0
      ? `\nProduct Image Links:\n${genData.imageUrls.map((u,i)=>`Image ${i+1}: ${u}`).join("\n")}`
      : "";
    const context = sel ? `Current SKU: ${sel.sku}\nPackage: ${sel.packageIncludes}${imageLinksText}\nGenerated content:\n${JSON.stringify(genData||{},null,2)}` : "";

    try {
      const r = await fetch("/api/listing/chat", {
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ message: userMsg, context, history: chatMessages }),
      });
      const d = await r.json();
      if (!d.ok) throw new Error(d.error);

      if (d.usage) {
        setUsage(u=>({
          inputTokens:  u.inputTokens  + (d.usage.inputTokens||0),
          outputTokens: u.outputTokens + (d.usage.outputTokens||0),
          calls: u.calls + 1,
        }));
      }

      setChatMessages(m=>[...m,{role:"ai",text:d.reply}]);

      // If AI returns JSON content, offer to apply it
      if (d.updatedContent && selectedSku) {
        setGenerated(g=>({...g,[selectedSku]:{...g[selectedSku],...d.updatedContent}}));
        setChatMessages(m=>[...m,{role:"system",text:"✅ Content updated automatically from AI response"}]);
      }
    } catch(e) {
      setChatMessages(m=>[...m,{role:"error",text:"❌ "+e.message}]);
    }
    setChatting(false);
  };

  const downloadExcel = async () => {
    setDownloadingAll(true);
    const rows = listingRows.map(r=>({...r,...(generated[r.sku]||{})}));
    const fd = new FormData();
    fd.append("rows", JSON.stringify(rows));
    try {
      const res = await fetch("/api/listing/download",{method:"POST",body:fd});
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href=url; a.download="listing_filled.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch(e) { alert("Download failed: "+e.message); }
    setDownloadingAll(false);
  };

  const doneCount  = Object.keys(generated).length;
  const totalCount = listingRows.length;
  const updateField = (sku,field,val) => setGenerated(g=>({...g,[sku]:{...g[sku],[field]:val}}));
  const cost = calcCost(usage.inputTokens, usage.outputTokens);

  return (
    <div style={S.page}>
      <div style={S.gridBg}/>
      <div style={S.wrap}>
        {/* Header */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:24}}>
          <div style={{display:"flex",alignItems:"center",gap:12}}>
            <button style={S.ghost} onClick={onBack}>← Dashboard</button>
            <div style={{...S.badge,background:`${COLOR}18`,border:`1px solid ${COLOR}44`,color:COLOR}}>✍️ Listing Generator</div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:16}}>
            {usage.calls>0&&(
              <div style={{background:"rgba(39,174,96,0.1)",border:"1px solid rgba(39,174,96,0.2)",borderRadius:8,padding:"6px 12px",fontSize:11}}>
                <span style={{color:"#27AE60",fontWeight:700}}>📊 Usage: </span>
                <span style={{color:"#aabbcc"}}>{usage.calls} calls · {(usage.inputTokens+usage.outputTokens).toLocaleString()} tokens · </span>
                <span style={{color:"#F39C12",fontWeight:700}}>₹{cost.inr} (~${cost.usd})</span>
              </div>
            )}
            <span style={{color:"#8899aa",fontSize:13}}>👤 {user}</span>
          </div>
        </div>

        <h1 style={{color:"#e8edf5",fontSize:22,fontWeight:800,margin:"0 0 4px"}}>Listing Content Generator</h1>
        <p style={{color:"#8899aa",fontSize:13,marginBottom:24}}>AI generates Title, Bullets, Sales Package & Description. Images auto-detected from server.</p>

        {step==="setup" && (
          <SetupStep listingRef={listingRef} refRef={refRef} kwRef={kwRef}
            amzGuideRef={amzGuideRef} fkGuideRef={fkGuideRef} msGuideRef={msGuideRef}
            refUrls={refUrls} setRefUrls={setRefUrls} platform={platform} setPlatform={setPlatform}
            onParse={handleParse} parsing={parsing} parseError={parseError}/>
        )}

        {step==="preview" && (
          <div style={{display:"grid",gridTemplateColumns:"260px 1fr 300px",gap:16,alignItems:"start"}}>
            {/* SKU List */}
            <div style={{...S.card,padding:0,overflow:"hidden"}}>
              <div style={{padding:"10px 14px",borderBottom:"1px solid rgba(255,255,255,0.06)",display:"flex",justifyContent:"space-between"}}>
                <span style={{color:"#e8edf5",fontWeight:700,fontSize:13}}>{totalCount} SKUs</span>
                <span style={{color:doneCount===totalCount?"#27AE60":COLOR,fontSize:12,fontWeight:600}}>{doneCount}/{totalCount}</span>
              </div>
              <div style={{height:3,background:"rgba(255,255,255,0.05)"}}>
                <div style={{height:"100%",background:`linear-gradient(90deg,#E67E22,${COLOR})`,width:`${totalCount?(doneCount/totalCount)*100:0}%`,transition:"width 0.3s"}}/>
              </div>
              <div style={{maxHeight:400,overflowY:"auto"}}>
                {listingRows.map(row=>{
                  const done=!!generated[row.sku],err=errors[row.sku],gen=generating[row.sku],isSel=selectedSku===row.sku;
                  return (
                    <div key={row.sku} onClick={()=>setSelectedSku(row.sku)}
                      style={{padding:"8px 12px",borderBottom:"1px solid rgba(255,255,255,0.04)",cursor:"pointer",
                        background:isSel?"rgba(243,156,18,0.1)":done?"rgba(39,174,96,0.03)":"transparent",
                        borderLeft:isSel?`3px solid ${COLOR}`:"3px solid transparent"}}>
                      <div style={{display:"flex",justifyContent:"space-between",marginBottom:2}}>
                        <span style={{color:"#e8edf5",fontSize:11,fontWeight:600}}>{row.sku}</span>
                        <span style={{fontSize:11}}>{gen?"⟳":done?"✅":err?"❌":"○"}</span>
                      </div>
                      <div style={{color:"#667788",fontSize:10,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{row.packageIncludes}</div>
                    </div>
                  );
                })}
              </div>
              <div style={{padding:"10px 12px",borderTop:"1px solid rgba(255,255,255,0.06)",display:"flex",flexDirection:"column",gap:6}}>
                <button style={{...S.btn,background:"linear-gradient(135deg,#E67E22,#F39C12)",fontSize:12,padding:"8px"}} onClick={generateAll}>
                  ⚡ Generate All
                </button>
                <button style={{...S.btn,background:doneCount?"linear-gradient(135deg,#1E8449,#27AE60)":"rgba(255,255,255,0.08)",
                  color:doneCount?"#fff":"#8899aa",fontSize:12,padding:"8px",
                  ...(downloadingAll?{opacity:0.6,cursor:"not-allowed"}:{})}}
                  onClick={downloadExcel} disabled={downloadingAll||!doneCount}>
                  {downloadingAll?"⟳ ...":"⬇ Download Excel"}
                </button>
                <button style={{...S.ghost,fontSize:11,textAlign:"center"}} onClick={()=>setStep("setup")}>← Change Files</button>
              </div>
            </div>

            {/* Content Panel */}
            <div>
              {!selectedSku ? (
                <div style={{...S.card,textAlign:"center",padding:"48px 20px"}}>
                  <div style={{fontSize:36,marginBottom:10}}>👈</div>
                  <div style={{color:"#8899aa",fontSize:14}}>Select a SKU to generate or edit content</div>
                </div>
              ) : (() => {
                const sel = listingRows.find(r=>r.sku===selectedSku);
                const genData = generated[selectedSku];
                const isGen = generating[selectedSku];
                const isEdit = editMode[selectedSku];
                return (
                  <div style={S.card}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,paddingBottom:12,borderBottom:"1px solid rgba(255,255,255,0.07)"}}>
                      <div>
                        <div style={{color:COLOR,fontWeight:800,fontSize:15}}>{selectedSku}</div>
                        <div style={{color:"#8899aa",fontSize:11,marginTop:2}}>₹{sel?.price} · MRP ₹{sel?.mrp} · {sel?.packageIncludes}</div>
                      </div>
                      <div style={{display:"flex",gap:8}}>
                        {genData&&<button style={{...S.ghost,fontSize:11}} onClick={()=>setEditMode(e=>({...e,[selectedSku]:!e[selectedSku]}))}>
                          {isEdit?"✓ Done":"✏️ Edit"}
                        </button>}
                        <button style={{...S.btn,background:`linear-gradient(135deg,#E67E22,${COLOR})`,fontSize:12,padding:"7px 14px",
                          ...(isGen?{opacity:0.6,cursor:"not-allowed"}:{})}}
                          onClick={()=>generateSku(sel)} disabled={isGen}>
                          {isGen?"⟳ Generating...":(genData?"🔄 Regenerate":"⚡ Generate")}
                        </button>
                      </div>
                    </div>

                    {/* Image thumbnails */}
                    {genData?.imageUrls?.length>0&&(
                      <div style={{display:"flex",gap:6,marginBottom:12,flexWrap:"wrap",alignItems:"center"}}>
                        {genData.imageUrls.map((url,i)=>(
                          <img key={i} src={url} alt={i} style={{width:48,height:48,objectFit:"cover",borderRadius:6,border:"1px solid rgba(255,255,255,0.1)"}} onError={e=>e.target.style.display="none"}/>
                        ))}
                        <span style={{color:"#667788",fontSize:10}}>{genData.imageUrls.length} image{genData.imageUrls.length>1?"s":""} used for analysis</span>
                      </div>
                    )}

                    {errors[selectedSku]&&(
                      <div style={{color:"#FF6B6B",fontSize:12,background:"rgba(255,107,107,0.1)",border:"1px solid rgba(255,107,107,0.3)",borderRadius:8,padding:"8px 12px",marginBottom:10}}>❌ {errors[selectedSku]}</div>
                    )}

                    {isGen&&(
                      <div style={{textAlign:"center",padding:"32px 0",color:COLOR}}>
                        <div style={{fontSize:28,marginBottom:6}}>✍️</div>
                        <div style={{fontSize:13}}>AI is writing content for {selectedSku}...</div>
                      </div>
                    )}

                    {genData&&!isGen&&(
                      <div style={{display:"flex",flexDirection:"column",gap:10}}>
                        <CF label="Title" field="title" sku={selectedSku} value={genData.title} isEdit={isEdit} onChange={updateField} rows={2} limit={200}/>
                        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                          {["bp1","bp2","bp3","bp4","bp5","bp6","bp7","bp8","bp9","bp10"].filter(bp=>genData[bp]).map((bp,i)=>(
                            <CF key={bp} label={`Bullet ${i+1}`} field={bp} sku={selectedSku} value={genData[bp]} isEdit={isEdit} onChange={updateField} rows={2} limit={200}/>
                          ))}
                          {isEdit && ["bp1","bp2","bp3","bp4","bp5","bp6","bp7","bp8","bp9","bp10"].filter(bp=>!genData[bp]).slice(0,1).map(bp=>(
                            <CF key={bp} label={`+ Add Bullet`} field={bp} sku={selectedSku} value="" isEdit={true} onChange={updateField} rows={2} limit={200}/>
                          ))}
                        </div>
                        {/* Image links */}
                        {genData.imageUrls?.length>0&&(
                          <div style={{background:"rgba(39,174,96,0.06)",border:"1px solid rgba(39,174,96,0.15)",borderRadius:8,padding:"10px 12px"}}>
                            <div style={{color:"#27AE60",fontSize:11,fontWeight:600,marginBottom:6}}>🖼️ Product Image Links</div>
                            <div style={{display:"flex",flexDirection:"column",gap:4}}>
                              {genData.imageUrls.map((url,i)=>(
                                <div key={i} style={{display:"flex",alignItems:"center",gap:8}}>
                                  <span style={{color:"#8899aa",fontSize:11,minWidth:60}}>Image {i+1}:</span>
                                  <input style={{...S.input,fontSize:10,padding:"4px 8px",flex:1}} value={url} readOnly onClick={e=>e.target.select()}/>
                                  <button onClick={()=>navigator.clipboard.writeText(url)}
                                    style={{background:"rgba(74,159,212,0.15)",border:"1px solid rgba(74,159,212,0.3)",borderRadius:5,color:"#4A9FD4",cursor:"pointer",fontSize:10,padding:"3px 8px",flexShrink:0}}>
                                    Copy
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:6}}>
                          {["sp1","sp2","sp3","sp4","sp5"].map((sp,i)=>(
                            genData[sp]&&<CF key={sp} label={`Pkg ${i+1}`} field={sp} sku={selectedSku} value={genData[sp]} isEdit={isEdit} onChange={updateField} rows={1}/>
                          ))}
                        </div>
                        <CF label="Description" field="description" sku={selectedSku} value={genData.description} isEdit={isEdit} onChange={updateField} rows={3} limit={500}/>
                      </div>
                    )}

                    {!genData&&!isGen&&!errors[selectedSku]&&(
                      <div style={{textAlign:"center",padding:"32px 0",color:"#667788"}}>
                        <div style={{fontSize:28,marginBottom:6}}>✍️</div>
                        <div style={{fontSize:13}}>Click <strong style={{color:COLOR}}>Generate</strong> to create AI content</div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Chat Panel */}
            <div style={{...S.card,display:"flex",flexDirection:"column",height:"fit-content",maxHeight:680}}>
              <div style={{borderBottom:"1px solid rgba(255,255,255,0.07)",paddingBottom:10,marginBottom:10}}>
                <div style={{color:"#e8edf5",fontWeight:700,fontSize:13}}>💬 Chat with Gemini</div>
                <div style={{color:"#667788",fontSize:10,marginTop:2}}>
                  {selectedSku?`Context: ${selectedSku}`:"Select a SKU for context"}
                </div>
              </div>

              {/* Messages */}
              <div style={{flex:1,overflowY:"auto",maxHeight:440,display:"flex",flexDirection:"column",gap:8,marginBottom:10}}>
                {chatMessages.length===0&&(
                  <div style={{color:"#445566",fontSize:12,textAlign:"center",padding:"20px 0"}}>
                    Ask Gemini to refine content, suggest better keywords, change tone, or rewrite any section.
                    <br/><br/>
                    <span style={{color:"#334455",fontSize:11}}>
                      e.g. "Rewrite bullet 1 without the word cheap"<br/>
                      "Make the title more festive"<br/>
                      "Add keyword 'Rath Yatra decoration'"
                    </span>
                  </div>
                )}
                {chatMessages.map((m,i)=>(
                  <div key={i} style={{
                    padding:"8px 10px",borderRadius:8,fontSize:12,lineHeight:1.5,
                    background:m.role==="user"?"rgba(243,156,18,0.12)":m.role==="system"?"rgba(39,174,96,0.1)":m.role==="error"?"rgba(255,107,107,0.1)":"rgba(255,255,255,0.04)",
                    color:m.role==="user"?COLOR:m.role==="system"?"#27AE60":m.role==="error"?"#FF6B6B":"#ccd",
                    alignSelf:m.role==="user"?"flex-end":"flex-start",
                    maxWidth:"95%",
                    borderLeft:m.role==="ai"?`2px solid rgba(176,127,212,0.4)`:"none",
                  }}>
                    {m.role==="user"&&<span style={{fontSize:10,color:"#E67E22",display:"block",marginBottom:2}}>You</span>}
                    {m.role==="ai"&&<span style={{fontSize:10,color:"#B07FD4",display:"block",marginBottom:2}}>Gemini</span>}
                    <span style={{whiteSpace:"pre-wrap"}}>{m.text}</span>
                  </div>
                ))}
                {chatting&&(
                  <div style={{padding:"8px 10px",borderRadius:8,fontSize:12,color:"#B07FD4",background:"rgba(255,255,255,0.04)",borderLeft:"2px solid rgba(176,127,212,0.4)"}}>
                    <span style={{fontSize:10,display:"block",marginBottom:2}}>Gemini</span>
                    ⟳ Thinking...
                  </div>
                )}
                <div ref={chatEndRef}/>
              </div>

              {/* Input */}
              <div style={{display:"flex",gap:6}}>
                <textarea
                  style={{...S.input,resize:"none",fontSize:12,flex:1,minHeight:60}}
                  placeholder="Ask Gemini to refine content..."
                  value={chatInput}
                  onChange={e=>setChatInput(e.target.value)}
                  onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();sendChat();}}}
                />
                <button style={{...S.btn,background:"linear-gradient(135deg,#4A235A,#B07FD4)",padding:"8px 12px",fontSize:13,alignSelf:"flex-end",...(chatting?{opacity:0.6,cursor:"not-allowed"}:{})}}
                  onClick={sendChat} disabled={chatting}>
                  ↑
                </button>
              </div>
              <div style={{color:"#334455",fontSize:10,marginTop:4}}>Enter to send · Shift+Enter for new line</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CF({label,field,sku,value,isEdit,onChange,rows=2,limit}) {
  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
        <label style={S.label}>{label}</label>
        {limit&&value&&<span style={{fontSize:10,color:value.length>limit?"#E74C3C":"#667788"}}>{value.length}/{limit}</span>}
      </div>
      {isEdit
        ?<textarea style={{...S.input,resize:"vertical",lineHeight:1.5,fontSize:12}} rows={rows} value={value||""} onChange={e=>onChange(sku,field,e.target.value)}/>
        :<div style={{background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:8,padding:"8px 11px",fontSize:12,color:"#ccd",lineHeight:1.6,minHeight:rows*20,whiteSpace:"pre-wrap"}}>
          {value||<span style={{color:"#445566",fontStyle:"italic"}}>Not generated</span>}
        </div>
      }
    </div>
  );
}

function SetupStep({listingRef,refRef,kwRef,amzGuideRef,fkGuideRef,msGuideRef,refUrls,setRefUrls,platform,setPlatform,onParse,parsing,parseError}) {
  return (
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20}}>
      <div style={{display:"flex",flexDirection:"column",gap:16}}>
        <div style={{...S.card,borderColor:"rgba(243,156,18,0.3)"}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
            <span style={{fontSize:20}}>📋</span>
            <div><div style={{color:"#e8edf5",fontWeight:700,fontSize:14}}>Listing Template</div>
            <div style={{color:"#8899aa",fontSize:11}}>Required — SKU, Package Includes, Price, MRP</div></div>
            <span style={{marginLeft:"auto",background:"rgba(231,76,60,0.2)",color:"#E74C3C",fontSize:10,padding:"2px 8px",borderRadius:10,fontWeight:600}}>Required</span>
          </div>
          <label style={{display:"block",background:"rgba(243,156,18,0.08)",border:"2px dashed rgba(243,156,18,0.3)",borderRadius:8,padding:"16px",cursor:"pointer",textAlign:"center"}}>
            <input ref={listingRef} type="file" accept=".xlsx,.xls,.csv" style={{display:"none"}}
              onChange={e=>e.target.files[0]&&(document.getElementById("ln").textContent=e.target.files[0].name)}/>
            <div style={{color:"#F39C12",fontSize:13,fontWeight:600}}>Click to upload</div>
            <div id="ln" style={{color:"#8899aa",fontSize:11,marginTop:4}}>listing_generate.xlsx</div>
          </label>
        </div>
        <div style={S.card}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
            <span style={{fontSize:20}}>🔑</span>
            <div><div style={{color:"#e8edf5",fontWeight:700,fontSize:14}}>Keywords File</div>
            <div style={{color:"#8899aa",fontSize:11}}>Optional — search term report or keyword list</div></div>
            <span style={{marginLeft:"auto",background:"rgba(74,159,212,0.1)",color:"#4A9FD4",fontSize:10,padding:"2px 8px",borderRadius:10,fontWeight:600}}>Optional</span>
          </div>
          <label style={{display:"block",background:"rgba(74,159,212,0.05)",border:"2px dashed rgba(74,159,212,0.2)",borderRadius:8,padding:"14px",cursor:"pointer",textAlign:"center"}}>
            <input ref={kwRef} type="file" accept=".xlsx,.xls,.csv" style={{display:"none"}}
              onChange={e=>e.target.files[0]&&(document.getElementById("kn").textContent=e.target.files[0].name)}/>
            <div style={{color:"#4A9FD4",fontSize:12,fontWeight:600}}>Click to upload</div>
            <div id="kn" style={{color:"#8899aa",fontSize:11,marginTop:3}}>keywords.xlsx</div>
          </label>
        </div>
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:16}}>
        <div style={S.card}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
            <span style={{fontSize:20}}>📝</span>
            <div><div style={{color:"#e8edf5",fontWeight:700,fontSize:14}}>Reference Content</div>
            <div style={{color:"#8899aa",fontSize:11}}>Optional — Title options, Bullet options Excel</div></div>
            <span style={{marginLeft:"auto",background:"rgba(176,127,212,0.1)",color:"#B07FD4",fontSize:10,padding:"2px 8px",borderRadius:10,fontWeight:600}}>Optional</span>
          </div>
          <label style={{display:"block",background:"rgba(176,127,212,0.05)",border:"2px dashed rgba(176,127,212,0.2)",borderRadius:8,padding:"14px",cursor:"pointer",textAlign:"center"}}>
            <input ref={refRef} type="file" accept=".xlsx,.xls,.csv" style={{display:"none"}}
              onChange={e=>e.target.files[0]&&(document.getElementById("rn").textContent=e.target.files[0].name)}/>
            <div style={{color:"#B07FD4",fontSize:12,fontWeight:600}}>Click to upload</div>
            <div id="rn" style={{color:"#8899aa",fontSize:11,marginTop:3}}>reference_content.xlsx</div>
          </label>
        </div>
        <div style={S.card}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
            <span style={{fontSize:20}}>🔗</span>
            <div><div style={{color:"#e8edf5",fontWeight:700,fontSize:14}}>Reference URLs</div>
            <div style={{color:"#8899aa",fontSize:11}}>Optional — competitor listing URLs for style</div></div>
            <span style={{marginLeft:"auto",background:"rgba(39,174,96,0.1)",color:"#27AE60",fontSize:10,padding:"2px 8px",borderRadius:10,fontWeight:600}}>Optional</span>
          </div>
          {refUrls.map((url,i)=>(
            <input key={i} style={{...S.input,marginBottom:6,fontSize:11}}
              placeholder={`Reference URL ${i+1}`} value={url}
              onChange={e=>setRefUrls(u=>{const n=[...u];n[i]=e.target.value;return n;})}/>
          ))}
        </div>
      </div>
      {/* Platform Guidelines */}
      <div style={{gridColumn:"1/-1"}}>
        <div style={{...S.card,marginBottom:12}}>
          <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:14}}>
            <span style={{fontSize:18}}>📐</span>
            <div><div style={{color:"#e8edf5",fontWeight:700,fontSize:14}}>Platform Guidelines</div>
            <div style={{color:"#8899aa",fontSize:11}}>Optional — upload Amazon/Flipkart/Meesho listing guidelines. AI will follow character limits & rules.</div></div>
          </div>
          <div style={{display:"flex",gap:10,marginBottom:12,flexWrap:"wrap"}}>
            {[["amazon","📦 Amazon","#FF9900"],["flipkart","🛒 Flipkart","#2874F0"],["meesho","🛍️ Meesho","#F43397"]].map(([p,label,color])=>(
              <button key={p} onClick={()=>setPlatform(p)}
                style={{padding:"6px 16px",borderRadius:20,border:`1px solid ${platform===p?color+"66":"rgba(255,255,255,0.1)"}`,background:platform===p?color+"18":"transparent",color:platform===p?color:"#8899aa",cursor:"pointer",fontSize:12,fontWeight:600}}>
                {label}
              </button>
            ))}
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10}}>
            {[["amazon_guide","📦 Amazon Guidelines","#FF9900",amzGuideRef],["flipkart_guide","🛒 Flipkart Guidelines","#2874F0",fkGuideRef],["meesho_guide","🛍️ Meesho Guidelines","#F43397",msGuideRef]].map(([id,label,color,ref])=>(
              <label key={id} style={{display:"flex",flexDirection:"column",alignItems:"center",background:`${color}08`,border:`2px dashed ${color}30`,borderRadius:8,padding:"12px",cursor:"pointer",textAlign:"center"}}>
                <input ref={ref} type="file" accept=".xlsx,.xls,.csv,.txt,.pdf" style={{display:"none"}}
                  onChange={e=>e.target.files[0]&&(document.getElementById(id+"_name").textContent=e.target.files[0].name)}/>
                <span style={{color,fontSize:12,fontWeight:600,marginBottom:3}}>{label}</span>
                <span id={id+"_name"} style={{color:"#8899aa",fontSize:10}}>Click to upload</span>
              </label>
            ))}
          </div>
        </div>
      </div>
      <div style={{gridColumn:"1/-1",display:"flex",flexDirection:"column",alignItems:"center",gap:10}}>
        {parseError&&<div style={{color:"#FF6B6B",fontSize:13,background:"rgba(255,107,107,0.1)",border:"1px solid rgba(255,107,107,0.3)",borderRadius:8,padding:"8px 16px"}}>{parseError}</div>}
        <button style={{...S.btn,background:`linear-gradient(135deg,#E67E22,${COLOR})`,...(parsing?{opacity:0.6,cursor:"not-allowed"}:{})}}
          onClick={onParse} disabled={parsing}>
          {parsing?"⟳ Parsing...":"→ Load & Continue"}
        </button>
      </div>
    </div>
  );
}
